// ─────────────────────────────────────────────────────────────────────────────
// src/components/Layout.tsx  (version avec onglets Chrome-style)
// ─────────────────────────────────────────────────────────────────────────────
//
// Structure:
//  ┌───────────────────────────────────────────────────┐  ← AppBar  (62 px) FIXE
//  │                    AppBar                         │
//  ├───────────────────────────────────────────────────┤  ← TabBar  (40 px) FIXE
//  │  ⌂  │  ⬡ MDX Query Builder  ✕  │  🎨 Couleur ✕  │+│
//  ├───────────────────────────────────────────────────┤
//  │             Contenu de l'onglet actif             │  ← SCROLLABLE
//  │                    ...                            │
//  ├───────────────────────────────────────────────────┤
//  │                    Footer                         │  ← dans la zone scrollable
//  └───────────────────────────────────────────────────┘
//
// Chaque onglet mémorise sa "page" et les onglets persistent dans localStorage.
// Après déconnexion puis reconnexion, les onglets sont restaurés.
//
// Usage dans App.tsx :
//
//   import Layout from "./components/Layout";
//
//   // Supprimer le state "page" géré manuellement — Layout s'en occupe.
//   // Passer le renderPage existant en prop.
//
//   <Layout
//     renderPage={(page, setPage) => {
//       if (page === "accueil")  return <AccueilPage setPage={setPage} />;
//       if (page === "mdx")      return <QueryBuilder />;
//       if (page === "couleur")  return <CouleurView />;
//       // … autres pages
//     }}
//     onLogout={handleLogout}
//   />
//
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback } from "react";
import AppBar, { Page } from "./AppBar";
import Footer from "./Footer";
import { useTabs } from "./useTabs";
import TabBar from "./TabBar";

// ── Props ─────────────────────────────────────────────────────────────────────

interface LayoutProps {
  /**
   * Fonction qui reçoit la page courante de l'onglet actif et rend le contenu.
   * `setPage` permet à une page de naviguer vers une autre (ouvre un onglet).
   */
  renderPage: (page: Page, openTab: (p: Page) => void) => React.ReactNode;

  /** Callback appelé quand l'utilisateur clique sur "Déconnexion" */
  onLogout?: () => void;
  userRole?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Destructurer userRole dans les props
const Layout: React.FC<LayoutProps> = ({ renderPage, onLogout, userRole }) => {
  const {
    tabs,
    activeTabId,
    activeTab,
    openTab,
    closeTab,
    activateTab,
    moveTab,
    closeOtherTabs,
    closeAllTabs,
    duplicateTab,
  } = useTabs();

  // ── AppBar navigation → ouvre/active un onglet ───────────────────────────
  const handleSetPage = useCallback(
    (page: Page) => {
      openTab(page);
    },
    [openTab],
  );

  const currentPage: Page = activeTab?.page ?? "accueil";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      {/* ── AppBar — FIXE en haut ── */}
      <div style={{ flexShrink: 0 }}>
<AppBar
  page={currentPage}
  setPage={handleSetPage}
  onLogout={onLogout}
  userRole={userRole}   
/>
      </div>

      {/* ── TabBar — FIXE sous l'AppBar ── */}
      <div style={{ flexShrink: 0 }}>
        <TabBar
          tabs={tabs}
          activeTabId={activeTabId}
          onActivate={activateTab}
          onClose={closeTab}
          onNewTab={openTab}
          onMoveTab={moveTab}
          onCloseOthers={closeOtherTabs}
          onCloseAll={closeAllTabs}
          onDuplicate={duplicateTab}
        />
      </div>

      {/* ── Zone principale scrollable ── */}
      {/*
        On garde tous les panneaux dans le DOM (display: none pour les inactifs)
        afin que chaque onglet préserve son état React local (filtres, données…).
        Le Footer est inclus DANS chaque panneau scrollable — il apparaît
        donc après le contenu en bas de page, et non collé à l'écran.
      */}
      <main
        style={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {tabs.map((tab) => (
          <div
            key={tab.id}
            style={{
              position: "absolute",
              inset: 0,
              overflowY: "auto",
              // Écrans étroits : les pages larges (tableaux) défilent au lieu d'être coupées
              overflowX: "auto",
              display: tab.id === activeTabId ? "block" : "none",
              background: "#F8FAFC",
            }}
          >
            {/* Contenu de la page */}
            {renderPage(tab.page, openTab)}

            {/* ✅ Footer dans la zone scrollable — visible en bas de page */}
            <Footer />
          </div>
        ))}
      </main>
    </div>
  );
};

export default Layout;
